import { useState } from "react";
import { db } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { useSession } from "../lib/session";
import { fmtDate, useToast } from "./ui";

type C = { ok: boolean; basis: string | null; until: string | null; kind: string | null; given_on: string | null; source: string | null; note: string | null; has_email: boolean };
const SOURCES = ["Said yes on a call", "Said yes in person", "Signed form", "Replied by email", "Website form", "Event sign-up"];

/** Newsletter consent on a person's profile (Canada's anti-spam law). */
export function PersonConsent({ person }: { person: { id: string; full_name: string } }) {
  const { can } = useSession();
  const toast = useToast();
  const q = useQuery(`consent:${person.id}`, () => db.rpc<C>("person_consent", { p_person: person.id }));
  const [mode, setMode] = useState<"" | "Express" | "Enquiry">("");
  const [source, setSource] = useState(SOURCES[0]);
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState("");
  const c = q.data;
  if (!can("newsletter") && !can("leads", "edit")) return null;

  async function save(kind: "Express" | "Enquiry" | "Withdrawn", src: string, given = on) {
    setErr("");
    try {
      await db.upsert("marketing_consent", { person_id: person.id, kind, source: src, given_on: given }, "person_id");
      invalidate(`consent:${person.id}`); invalidate("nl:");
      toast(kind === "Withdrawn" ? `${person.full_name} won't get newsletters` : "Consent saved");
      setMode("");
    } catch (e) { setErr((e as Error).message); }
  }
  async function clear() {
    await db.remove("marketing_consent", { person_id: `eq.${person.id}` });
    invalidate(`consent:${person.id}`); invalidate("nl:");
  }

  return (
    <div className="appt-person">
      <div className="sol-head"><strong>Newsletter</strong></div>
      {!c ? <div className="small muted">…</div> : <>
        <div className="small">
          {!c.has_email ? <span className="muted">No email address.</span>
            : c.kind === "Withdrawn" ? <><span className="pill alert">Unsubscribed</span> <span className="muted">{c.source} · {fmtDate(c.given_on)}</span></>
            : c.ok ? <><span className="pill warm">Gets it</span> <span className="muted">{c.basis}{c.kind === "Express" && c.source ? ` · ${c.source}, ${fmtDate(c.given_on)}` : ""}{c.until ? ` · until ${fmtDate(c.until)}` : ""}</span></>
            : <><span className="pill">Not yet</span> <span className="muted">Hasn't agreed to newsletters{c.kind === "Enquiry" ? ` (enquiry from ${fmtDate(c.given_on)} has expired)` : ""}.</span></>}
        </div>
        {c.has_email && can("leads", "edit") && !mode && (
          <div className="toolbar" style={{ margin: "8px 0 0" }}>
            {c.kind !== "Express" && <button className="btn small" onClick={() => setMode("Express")}>They agreed</button>}
            {!c.ok && c.kind !== "Withdrawn" && <button className="btn small ghost" onClick={() => setMode("Enquiry")}>They enquired</button>}
            {c.kind !== "Withdrawn" && (c.ok || c.kind) && <button className="btn small ghost danger" onClick={() => save("Withdrawn", "Asked to stop", new Date().toISOString().slice(0, 10))}>Stop newsletters</button>}
            {c.kind && c.kind !== "Withdrawn" && <button className="btn small ghost" onClick={clear}>Clear</button>}
          </div>
        )}
        {mode && (
          <div className="toolbar" style={{ margin: "8px 0 0" }}>
            {mode === "Express"
              ? <select className="select" value={source} onChange={(e) => setSource(e.target.value)}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select>
              : <span className="small">Enquired on</span>}
            <input className="input" type="date" value={on} onChange={(e) => setOn(e.target.value)} style={{ maxWidth: 160 }} />
            <button className="btn small primary" onClick={() => save(mode, mode === "Express" ? source : "Enquiry")}>Save</button>
            <button className="btn small ghost" onClick={() => setMode("")}>Cancel</button>
          </div>
        )}
        {c.kind === "Withdrawn" && <div className="small muted" style={{ marginTop: 6 }}>Only add them back if they ask you to, in writing.</div>}
        {mode === "Express" && <div className="small muted" style={{ marginTop: 6 }}>Record how they said yes. You may need to show it later.</div>}
        {err && <div className="small" style={{ color: "var(--alert)" }}>{err}</div>}
      </>}
    </div>
  );
}
